import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  InputLabel,
  Box,
  Typography
} from "@mui/material";
import { CalendarToday, Event, Send } from "@mui/icons-material";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebaseConfig/firebase";

function OpdNextAppt({ opdId, currentAppt, patientInfo, onSaved }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Form states
  const [nextApptOption, setNextApptOption] = useState("none");
  const [nextApptType, setNextApptType] = useState("None");
  const [nextApptWork, setNextApptWork] = useState("");
  const [nextApptDate, setNextApptDate] = useState("");
  const [nextApptDuration, setNextApptDuration] = useState("30 Mins");
  const [loading, setLoading] = useState(false);

  // Sync state when modal opens or props change
  useEffect(() => {
    if (currentAppt) {
      setNextApptOption(currentAppt.nextApptOption || "none");
      setNextApptType(currentAppt.nextApptType || "None");
      setNextApptWork(currentAppt.nextApptWork || "");
      setNextApptDate(currentAppt.nextApptDate || "");
      setNextApptDuration(currentAppt.nextApptDuration || "30 Mins");
    }
  }, [currentAppt, open]);

  const handleOptionChange = (e) => {
    const val = e.target.value;
    setNextApptOption(val);
    if (val === "treatment") {
      setNextApptType("Treatment");
    } else if (val === "followup") {
      setNextApptType("Follow-Up");
    } else {
      setNextApptType("None");
    }
  };

  const handleOpen = (e) => {
    if (e) e.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const opdRef = doc(db, "opdEntry", opdId);
      await updateDoc(opdRef, {
        nextApptOption: nextApptOption || "none",
        nextApptType: nextApptType || "None",
        nextApptWork: nextApptOption === "none" ? "" : (nextApptWork || ""),
        nextApptDate: nextApptOption === "none" ? "" : (nextApptDate || ""),
        nextApptDuration: nextApptOption === "none" ? "" : (nextApptDuration || "30 Mins"),
        isApptGiven: false,
      });

      if (onSaved) onSaved();
      setOpen(false);
    } catch (err) {
      console.error("Error updating next appointment:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateToAppointment = async () => {
    // First save the appointment details
    await handleSave();
    // Then navigate to /appointment page with patient and appointment info
    navigate("/appointment", {
      state: {
        name: patientInfo?.ptName || patientInfo?.name || "",
        phoneNumber: patientInfo?.phNumber || patientInfo?.phoneNumber || "",
        id: patientInfo?.id || patientInfo?.ptId || "",
        originalptId: patientInfo?.originalptId || patientInfo?.id || "",
        photoURL: patientInfo?.photoURL || "",
        reason: nextApptWork,
        nextApptWork: nextApptWork,
        date: nextApptDate,
        nextApptDate: nextApptDate,
        duration: nextApptDuration,
        nextApptDuration: nextApptDuration,
        nextApptOption: nextApptOption,
        nextApptType: nextApptType,
        opdId: opdId,
      }
    });
  };

  return (
    <>
      <Button
        size="small"
        onClick={handleOpen}
        variant="outlined"
        startIcon={<CalendarToday style={{ fontSize: "14px" }} />}
        style={{
          fontSize: "11px",
          fontWeight: "600",
          textTransform: "none",
          borderRadius: "8px",
          borderColor: "rgba(5, 2, 43, 0.3)",
          color: "rgb(5, 2, 43)",
          padding: "4px 10px"
        }}
      >
        {currentAppt?.nextApptOption && currentAppt?.nextApptOption !== "none"
          ? "Edit Appt"
          : "Schedule Appt"}
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle
          style={{
            background: "linear-gradient(135deg, rgb(5, 2, 43), rgb(8, 10, 48))",
            color: "white",
            fontSize: "16px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <Event style={{ fontSize: "20px" }} />
          Set Next Appointment
        </DialogTitle>

        <DialogContent style={{ paddingTop: "20px" }}>
          <Box style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "8px" }}>
            {/* Option Radio Buttons */}
            <FormControl component="fieldset">
              <FormLabel
                component="legend"
                style={{ fontSize: "13px", fontWeight: "600", color: "#2c3e50" }}
              >
                Appointment Purpose
              </FormLabel>
              <RadioGroup
                row
                name="nextApptOption"
                value={nextApptOption}
                onChange={handleOptionChange}
              >
                <FormControlLabel
                  value="treatment"
                  control={<Radio size="small" color="primary" />}
                  label={<Typography style={{ fontSize: "13px" }}>Treatment</Typography>}
                />
                <FormControlLabel
                  value="followup"
                  control={<Radio size="small" color="primary" />}
                  label={<Typography style={{ fontSize: "13px" }}>Follow-Up (No SMS)</Typography>}
                />
                <FormControlLabel
                  value="none"
                  control={<Radio size="small" color="primary" />}
                  label={<Typography style={{ fontSize: "13px" }}>None</Typography>}
                />
              </RadioGroup>
            </FormControl>

            {nextApptOption !== "none" && (
              <>
                {/* Appointment Date */}
                <TextField
                  id="nextApptDate"
                  label="Next Appointment Date"
                  type="date"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={nextApptDate}
                  onChange={(e) => setNextApptDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                {/* Next Appointment Work */}
                <TextField
                  id="nextApptWork"
                  label="Procedure / Work Planned"
                  placeholder="e.g., RCT Sitting 2, Crown Placement, Scaling"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={nextApptWork}
                  onChange={(e) => setNextApptWork(e.target.value)}
                />

                {/* Next Appointment Duration */}
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="duration-label">Duration</InputLabel>
                  <Select
                    labelId="duration-label"
                    id="nextApptDuration"
                    value={nextApptDuration}
                    onChange={(e) => setNextApptDuration(e.target.value)}
                    label="Duration"
                  >
                    <MenuItem value="15 Mins">15 Mins</MenuItem>
                    <MenuItem value="30 Mins">30 Mins</MenuItem>
                    <MenuItem value="45 Mins">45 Mins</MenuItem>
                    <MenuItem value="60 Mins">60 Mins</MenuItem>
                    <MenuItem value="90 Mins">90 Mins</MenuItem>
                    <MenuItem value="120 Mins">120 Mins</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
          <Button onClick={handleClose} style={{ color: "#7f8c8d" }}>
            Cancel
          </Button>

          <Box style={{ display: "flex", gap: "8px" }}>
            <Button
              onClick={handleSave}
              disabled={loading}
              variant="outlined"
              style={{
                borderRadius: "8px",
                fontWeight: "bold",
                color: "rgb(5, 2, 43)",
                borderColor: "rgb(5, 2, 43)"
              }}
            >
              {loading ? "Saving..." : "Save Only"}
            </Button>

            <Button
              onClick={handleNavigateToAppointment}
              disabled={loading}
              variant="contained"
              style={{
                background: "linear-gradient(135deg, rgb(5, 2, 43), rgb(8, 10, 48))",
                color: "white",
                fontWeight: "bold",
                borderRadius: "8px"
              }}
            >
              Give Appointment
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default OpdNextAppt;
